import {
  AppInstanceAppearanceType,
  AppInstanceAssignmentType,
  AppInstanceCodeUploadShort,
  AppInstanceEmbeddedCodeShort,
  AppInstanceEmbeddedLinkShort,
  AppInstanceExternalLinkShort,
  AppInstanceFull,
  AppInstanceMiroShort,
  AppInstanceNativeLegacyCustomContext,
  AppInstanceNativeShort,
  AppInstancePageBuilderShort,
  AppInstanceShort,
  AppInstanceStatus,
  DefaultHierarchyLevelType,
  DevHubProductType,
  HierarchyCustomNodeType,
  HierarchyNodeType,
  MayBeNull,
  NavigationTreeMapping,
  NoCodeAppType,
  OsContextAppearance,
} from '@wpp-open/core'

import { LegacyAppWindowLibraryName } from 'legacy/types/apps'
import { LegacyMicroAppConfig, MicroAppLibraryType, MicroAppType, NativeMicroAppConfig } from 'types/apps/microApps'
import { NoCodeApp, NoCodeAppExternalLink } from 'types/apps/noCodeApps'
import { HierarchyTreeMapping } from 'types/hierarchy/hierarchy'
import { AppInstanceTemplate } from 'types/templates/templates'
import { excludeFalsy } from 'utils/common'
import { WorkspaceHierarchyIds } from 'utils/workspace'

export type AppInstanceApp = NativeMicroAppConfig | LegacyMicroAppConfig | NoCodeApp
export type RoutedAppInstanceApp = Exclude<AppInstanceApp, NoCodeAppExternalLink>

export const AppInstanceDataContextProject = 'PROJECT'

// This helper enforces an exhaustive check for isNoCodeAppInstanceApp()
const filterNoCodeAppInstanceApp = (app: AppInstanceApp): MayBeNull<NoCodeApp> =>
  app.type !== MicroAppType.Native && app.type !== MicroAppType.Legacy ? app : null

export const getAppInstanceName = (appInstance: AppInstanceFull | AppInstanceShort) =>
  appInstance.name ||
  (appInstance.devhubMetadata.productType === DevHubProductType.NATIVE_APPLICATION
    ? appInstance.devhubMetadata.version.name
    : appInstance.devhubMetadata.name)

export const canLaunchAppInstance = ({
  appInstance,
  targetAppearanceType,
}: {
  appInstance: AppInstanceFull | AppInstanceShort
  targetAppearanceType: AppInstanceAppearanceType
}) => {
  const app = mapAppInstanceToApp(appInstance)
  const isProjectOnly = !isNoCodeAppInstanceApp(app) && app.isProjectOnly

  return (
    !isProjectOnly &&
    !isAppInstanceStatusInactive(appInstance) &&
    isAppearanceSupported(targetAppearanceType, appInstance)
  )
}

export const getActiveAppInstanceStatuses = (appendStatuses?: AppInstanceStatus[]) => {
  return [AppInstanceStatus.Active, ...(appendStatuses || [])]
}

export const isAppInstanceStatusInactive = (appInstance: AppInstanceFull | AppInstanceShort | AppInstanceTemplate) => {
  const inactiveStatuses: AppInstanceStatus[] = [
    AppInstanceStatus.VersionDeleted,
    AppInstanceStatus.VersionAvailabilityDisabled,
    AppInstanceStatus.VersionHierarchyLevelDisabled,
  ]

  return !!appInstance.status && inactiveStatuses.includes(appInstance.status)
}

export const isValidAppearancesConfig = (appInstance: AppInstanceFull | AppInstanceShort) => {
  const appearances = appInstance.devhubMetadata.version.appearances

  return !!appearances && Object.keys(appearances).length > 0
}

export const isAppearanceSupported = (
  type: AppInstanceAppearanceType,
  appInstance: AppInstanceFull | AppInstanceShort,
) => !!appInstance.devhubMetadata.version.appearances?.[type]

export const isAppearanceInTemplateSupported = (type: AppInstanceAppearanceType, appInstance: AppInstanceTemplate) =>
  !!appInstance.appearances?.[type]

export const isAppInstanceInDevelopment = ({ status }: AppInstanceFull | AppInstanceShort | AppInstanceTemplate) =>
  status === AppInstanceStatus.VersionDevelopment

export const isAppInstanceDeleted = ({ status }: AppInstanceFull | AppInstanceShort) =>
  status === AppInstanceStatus.Deleted

export const formatAppCustomConfig = (appInstance: AppInstanceFull | AppInstanceShort) => {
  const customContext = appInstance.devhubMetadata.version.customContext || []

  return Object.fromEntries(customContext.map(({ key, value }) => [key, value]))
}

export const isNoCodeAppInstanceApp = (app: AppInstanceApp): app is NoCodeApp => !!filterNoCodeAppInstanceApp(app)

export const getAppInstanceAppearances = (appInstance: AppInstanceFull | AppInstanceShort): OsContextAppearance[] =>
  [
    isAppearanceSupported(AppInstanceAppearanceType.FULL_PAGE, appInstance) && OsContextAppearance.FullPage,
    isAppearanceSupported(AppInstanceAppearanceType.TOOL, appInstance) && OsContextAppearance.Tool,
    isAppearanceSupported(AppInstanceAppearanceType.WIDGET, appInstance) && OsContextAppearance.Widget,
  ].filter(excludeFalsy)

export const getDataContextRequirements = (
  dataContext: MayBeNull<string[]>,
): {
  requiredHierarchy: HierarchyNodeType[]
  isProjectOnly: boolean
} => {
  const context = dataContext || []

  const requiredHierarchy = context.filter(
    (value: string): value is HierarchyNodeType =>
      value === HierarchyCustomNodeType ||
      Object.values(DefaultHierarchyLevelType).includes(value as DefaultHierarchyLevelType),
  )
  const isProjectOnly = !!context.find(value => value === AppInstanceDataContextProject)

  return {
    requiredHierarchy,
    isProjectOnly,
  }
}

export const mapAppInstanceToApp = (appInstance: AppInstanceFull | AppInstanceShort): AppInstanceApp => {
  const stableId = appInstance.id
  const name = getAppInstanceName(appInstance)
  const appearances = getAppInstanceAppearances(appInstance)
  const noCodeAppType = appInstance.devhubMetadata.version.config?.appType

  // TODO: Needs refactoring. With proper appInstance typings these "as" casts should be removed

  if (noCodeAppType === NoCodeAppType.ExternalLink) {
    const externalLinkAppInstance = appInstance as AppInstanceExternalLinkShort
    let url = externalLinkAppInstance.devhubMetadata.version.config.url

    if (
      ((externalLinkAppInstance.devhubMetadata.isTemplate &&
        (externalLinkAppInstance.assignmentType === AppInstanceAssignmentType.Workspace ||
          externalLinkAppInstance.assignmentType === AppInstanceAssignmentType.Custom)) ||
        externalLinkAppInstance.assignmentType === AppInstanceAssignmentType.Template) &&
      externalLinkAppInstance.context.url
    ) {
      url = externalLinkAppInstance.context.url
    }

    return {
      type: NoCodeAppType.ExternalLink,
      stableId,
      name,
      appearances,
      url,
    }
  }

  const routedAppInstance = appInstance as Exclude<AppInstanceShort, AppInstanceExternalLinkShort>
  const osRoute = routedAppInstance.osRoute || routedAppInstance.devhubMetadata.version.routePath!

  if (!noCodeAppType) {
    const nativeAppInstance = routedAppInstance as AppInstanceNativeShort
    const libraryType =
      (nativeAppInstance.devhubMetadata.version.jsLibraryBundleType as MicroAppLibraryType) ||
      MicroAppLibraryType.SystemJS
    const bundleUrl = nativeAppInstance.devhubMetadata.version.jsBundleUrl || ''

    const { requiredHierarchy, isProjectOnly } = getDataContextRequirements(
      nativeAppInstance.devhubMetadata.version.dataContext,
    )

    const customContext = nativeAppInstance.devhubMetadata.version.customContext
    const isLegacy = !!customContext.find(item => item.key === 'legacy' && item.value === 'true')
    const windowLibraryName = customContext.find(item => item.key === 'windowLibraryName')?.value || null

    if (isLegacy) {
      const legacyCustomContext = customContext as AppInstanceNativeLegacyCustomContext
      const containerId = legacyCustomContext.find(item => item.key === 'containerId')?.value || ''

      return {
        type: MicroAppType.Legacy,
        stableId,
        name,
        appearances,
        osRoute,
        libraryType: MicroAppLibraryType.Window,
        windowLibraryName: windowLibraryName as LegacyAppWindowLibraryName,
        containerId,
        bundleUrl,
        permission: null,
        isProjectOnly,
        requiredHierarchy,
      }
    }

    return {
      type: MicroAppType.Native,
      stableId,
      name,
      appearances,
      osRoute,
      libraryType,
      windowLibraryName,
      bundleUrl,
      permission: null,
      isProjectOnly,
      requiredHierarchy,
    }
  }

  if (noCodeAppType === NoCodeAppType.EmbeddedLink) {
    const embeddedLinkAppInstance = routedAppInstance as AppInstanceEmbeddedLinkShort
    let url = embeddedLinkAppInstance.devhubMetadata.version.config.url

    if (
      ((embeddedLinkAppInstance.devhubMetadata.isTemplate &&
        (embeddedLinkAppInstance.assignmentType === AppInstanceAssignmentType.Workspace ||
          embeddedLinkAppInstance.assignmentType === AppInstanceAssignmentType.Custom)) ||
        embeddedLinkAppInstance.assignmentType === AppInstanceAssignmentType.Template) &&
      embeddedLinkAppInstance.context.url
    ) {
      url = embeddedLinkAppInstance.context.url
    }

    return {
      type: NoCodeAppType.EmbeddedLink,
      stableId,
      name,
      appearances,
      osRoute,
      url,
    }
  }

  if (noCodeAppType === NoCodeAppType.PageBuilder) {
    const pageBuilderAppInstance = routedAppInstance as AppInstancePageBuilderShort
    const externalConfigId = pageBuilderAppInstance.devhubMetadata.version.externalConfigId!

    return {
      type: NoCodeAppType.PageBuilder,
      stableId,
      name,
      appearances,
      osRoute,
      pageId: '',
      externalConfigId,
    }
  }

  if (noCodeAppType === NoCodeAppType.EmbeddedCode) {
    const embeddedCodeAppInstance = routedAppInstance as AppInstanceEmbeddedCodeShort
    let embeddedCode = embeddedCodeAppInstance.devhubMetadata.version.config.embeddedCode

    if (
      ((embeddedCodeAppInstance.devhubMetadata.isTemplate &&
        (embeddedCodeAppInstance.assignmentType === AppInstanceAssignmentType.Workspace ||
          embeddedCodeAppInstance.assignmentType === AppInstanceAssignmentType.Custom)) ||
        embeddedCodeAppInstance.assignmentType === AppInstanceAssignmentType.Template) &&
      embeddedCodeAppInstance.context.embeddedCode
    ) {
      embeddedCode = embeddedCodeAppInstance.context.embeddedCode
    }

    return {
      type: NoCodeAppType.EmbeddedCode,
      stableId,
      name,
      appearances,
      osRoute,
      embeddedCode,
    }
  }

  if (noCodeAppType === NoCodeAppType.MiroBoard) {
    const miroAppInstance = routedAppInstance as AppInstanceMiroShort
    let url: MayBeNull<string> = null

    if (miroAppInstance.assignmentType === AppInstanceAssignmentType.Workspace && miroAppInstance.context.url) {
      url = miroAppInstance.context.url
    }

    return {
      type: NoCodeAppType.MiroBoard,
      stableId,
      name,
      appearances,
      osRoute,
      url,
    }
  }

  if (noCodeAppType === NoCodeAppType.CodeUpload) {
    const codeUploadAppInstance = routedAppInstance as AppInstanceCodeUploadShort
    const homePageUrl = codeUploadAppInstance.devhubMetadata.version.config.homePageUrl

    return {
      type: NoCodeAppType.CodeUpload,
      stableId,
      name,
      appearances,
      osRoute,
      url: homePageUrl,
    }
  }

  throw new Error('Unsupported App Instance type')
}

/**
 * Checks whether a selected hierarchy matches the required hierarchy.
 *
 * @param hierarchyRequirements List of required hierarchy levels
 * @param hierarchyPathWithHiddenLevel List of selected hierarchy ids with a hidden level.
 * This hidden level is passed in app's context, so it's included in the validation.
 * @param mappingWithHiddenLevel Nav tree mapping with a hidden level.
 */
export const isValidHierarchyPath = ({
  hierarchyRequirements,
  hierarchyPathWithHiddenLevel,
  mappingWithHiddenLevel,
}: {
  hierarchyRequirements: HierarchyNodeType[]
  hierarchyPathWithHiddenLevel: WorkspaceHierarchyIds
  mappingWithHiddenLevel: NavigationTreeMapping
}) => {
  // Tenant level is always available
  const activeHierarchyLevelsWithHiddenLevel = [
    DefaultHierarchyLevelType.Tenant,
    ...hierarchyPathWithHiddenLevel.map(nodeAzId => mappingWithHiddenLevel[nodeAzId]?.type as HierarchyNodeType),
  ]

  return hierarchyRequirements.every(requiredLevel => activeHierarchyLevelsWithHiddenLevel.includes(requiredLevel))
}

/**
 * Checks whether a selected hierarchy is valid for a given app instance app.
 *
 * @param app App instance app that is checked
 * @param hierarchyPathWithHiddenLevel List of selected hierarchy ids with a hidden level.
 * This hidden level is passed in app's context, so it's included in the validation.
 * @param mappingWithHiddenLevel Nav tree mapping with a hidden level.
 */
export const isValidAppInstanceAppHierarchyPath = ({
  app,
  hierarchyPathWithHiddenLevel,
  mappingWithHiddenLevel,
}: {
  app: AppInstanceApp
  hierarchyPathWithHiddenLevel: WorkspaceHierarchyIds
  mappingWithHiddenLevel: NavigationTreeMapping | HierarchyTreeMapping
}) => {
  if (isNoCodeAppInstanceApp(app)) {
    return true
  }

  return isValidHierarchyPath({
    hierarchyRequirements: app.requiredHierarchy,
    hierarchyPathWithHiddenLevel,
    mappingWithHiddenLevel,
  })
}

export const getAppInstanceLogo = ({
  appInstance,
  getDevHubLogoUrl,
}: {
  appInstance: AppInstanceShort
  getDevHubLogoUrl: (key: string | undefined) => string
}) => {
  if (appInstance.logoThumbnail?.url) {
    return appInstance.logoThumbnail?.url
  } else if (appInstance.devhubMetadata.logoMeta?.thumbnail.key) {
    return getDevHubLogoUrl(appInstance.devhubMetadata.logoMeta?.thumbnail.key)
  } else if (appInstance.devhubMetadata.logoMeta?.thumbnail.downloadUrl) {
    return appInstance.devhubMetadata.logoMeta?.thumbnail.downloadUrl
  } else {
    return ''
  }
}
